

import { useDocument, useLease, useMandate, useProperty } from '@/OneLiners';
import { DocumentFormViewModel } from '../ViewModels';
import { useDocumentEnricher, useDocumentCategory } from ".";
import {
    ActionStatusHelper,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    ActionResponse,
    useFormLifeCycle,
} from "@/Shared";
import { DocumentValidationSchema } from "../DocumentFormValidation";
import { DocumentModel } from "../DocumentModel";


/**
 * =====================================================
 * Hook: useDocumentForm
 * =====================================================
 *
 * Description:
 * Custom hook to manage document form state, initialization,
 * enrichment, submission, and permissions.
 *
 * Responsibilities:
 * - Fetch and enrich the current document if editing
 * - Track form submission state and provide feedback
 * - Build view model with enumerations, resource lists, and enriched document
 * - Manage access rights and redirect on insufficient permissions
 *
 * Notes:
 * - Compatible with both modal and full-page form usage
 * - Integrates with toast notifications and app navigation
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */


/**
 * 
 * @param toastHook modalMode?: {
    id?: number;
    type: string;
    handleClose: () => void;
})
 * @param modalMode 
 * @returns 
 */
export const useDocumentForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, inModalHook, permissionHook, accessRight } = props;
    const { curType } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.Document;

    const { dataMap: leaseMap, stateResponse: leaseStateResponse } = useLease();
    const { dataMap: propertyMap, stateResponse: propertyStateResponse } = useProperty();
    const { dataMap: mandateMap, stateResponse: mandateStateResponse } = useMandate();


    /** =====================================================
     * Document CRUD Operations & Enrichment
     * =====================================================
     *
     * Hooks providing current document retrieval, create/update operations,
     * and enrichment with related entities.
     */
    const documentHook = useDocument();

    const {
        enricherResponse,
        getEnrichedDocument,
    } = useDocumentEnricher();


    const {
        categoryResponse,
        docCategoryList,
        curCategory,
        curType: actualType,
        setCurCategory
    } = useDocumentCategory(curType);

    /** =====================================================
     * Combined Response
     * =====================================================
     *
     * Combines loading and error states from all dependent hooks
     * into a single ActionResponse for form initialization.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            documentHook.stateResponse.status,
            enricherResponse.status,
            leaseStateResponse.status,
            propertyStateResponse.status,
            mandateStateResponse.status,
            categoryResponse.status,
            permissionHook.stateResponse.status
        ),

        error: documentHook.stateResponse.error ??
            enricherResponse.error ??
            leaseStateResponse.error ??
            propertyStateResponse.error ??
            mandateStateResponse.error ??
            categoryResponse.error
    };

    const { DocumentEnums, MandateEnums } = useAppEnum();

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: documentHook,
        buildVM: (currentEntity: DocumentModel) => {

            if (!curCategory && currentEntity?.documentCategory) {
                setCurCategory(currentEntity.documentCategory);
            }

            if (extraArgs) {
                const isCreateWithNoType = !documentHook.queryId && !actualType;
                const isUpdateWithNoModel = documentHook.queryId && !currentEntity;

                console.log(actualType);

                if (isCreateWithNoType || isUpdateWithNoModel) {
                    return undefined;
                }
            }
            return new DocumentFormViewModel(
                DocumentEnums,
                MandateEnums,
                leaseMap,
                propertyMap,
                mandateMap,
                getEnrichedDocument(currentEntity),
                curCategory,
                actualType,
                //entityId
            )
        }
    });

    /** =====================================================
     * Public API
     * =====================================================
     *
     * Exposes only the data and actions strictly required
     * by the component layer.
     */
    return {
        // Form
        ...lifeCycleHook,
        ValidationSchema: DocumentValidationSchema(DocumentEnums),
        isModalMode: !!inModalHook,
    }
}